import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Button, Switch } from '@lib/ui';

export interface SettingsSection {
  /** Unique identifier */
  id: string;
  /** Section title */
  title: string;
  /** Section description */
  description?: string;
  /** Section content */
  content: ReactNode;
}

export interface SettingsFormProps {
  /** Page title */
  title?: string;
  /** Page description */
  description?: string;
  /** Array of settings sections */
  sections: SettingsSection[];
  /** Called when reset is clicked */
  onReset?: () => void;
  /** Reset button label */
  resetLabel?: string;
  /** Called when save is clicked */
  onSave?: () => void;
  /** Save button label */
  saveLabel?: string;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Additional className */
  className?: string;
}

export function SettingsForm({
  title = 'Settings',
  description,
  sections,
  onReset,
  resetLabel = 'Reset to defaults',
  onSave,
  saveLabel = 'Save changes',
  isSaving,
  className,
}: SettingsFormProps) {
  return (
    <div className={clsx('w-full max-w-3xl', className)}>
      <h5 className="text-lg font-medium text-gray-800 dark:text-dark-50">{title}</h5>
      {description && (
        <p className="mt-0.5 text-sm text-balance text-gray-500 dark:text-dark-200">
          {description}
        </p>
      )}

      <div className="space-y-8 mt-6">
        {sections.map((section, index) => (
          <div key={section.id}>
            {index > 0 && <div className="h-px bg-gray-200 dark:bg-dark-500 mb-8" />}
            <div>
              <p className="text-base font-medium text-gray-800 dark:text-dark-100">
                {section.title}
              </p>
              {section.description && (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-dark-300">
                  {section.description}
                </p>
              )}
            </div>
            <div className="mt-4">{section.content}</div>
          </div>
        ))}
      </div>

      {(onReset || onSave) && (
        <div className="mt-10 flex gap-3">
          {onReset && (
            <Button variant="outlined" onClick={onReset}>
              {resetLabel}
            </Button>
          )}
          {onSave && (
            <Button color="primary" onClick={onSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : saveLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Helper components for common settings patterns

export interface SettingRowProps {
  /** Setting label */
  label: string;
  /** Setting description */
  description?: string;
  /** Control element */
  children: ReactNode;
  /** Additional className */
  className?: string;
}

export function SettingRow({ label, description, children, className }: SettingRowProps) {
  return (
    <div className={clsx('flex items-center justify-between gap-4', className)}>
      <div className="flex-1">
        <p className="font-medium text-gray-800 dark:text-dark-100">{label}</p>
        {description && (
          <p className="mt-0.5 text-sm text-gray-500 dark:text-dark-300">{description}</p>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

export interface SettingToggleProps {
  /** Setting label */
  label: string;
  /** Setting description */
  description?: string;
  /** Whether the toggle is checked */
  checked: boolean;
  /** Called when toggle changes */
  onChange: (checked: boolean) => void;
  /** Additional className */
  className?: string;
}

export function SettingToggle({
  label,
  description,
  checked,
  onChange,
  className,
}: SettingToggleProps) {
  return (
    <SettingRow label={label} description={description} className={className}>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </SettingRow>
  );
}

export interface SettingOptionProps<T> {
  /** Array of options */
  options: Array<{
    value: T;
    label: string;
    description?: string;
    preview?: ReactNode;
  }>;
  /** Currently selected value */
  value: T;
  /** Called when selection changes */
  onChange: (value: T) => void;
  /** Layout direction */
  layout?: 'horizontal' | 'vertical';
  /** Additional className */
  className?: string;
}

export function SettingOptions<T extends string | number>({
  options,
  value,
  onChange,
  layout = 'horizontal',
  className,
}: SettingOptionProps<T>) {
  return (
    <div
      className={clsx('flex gap-4', layout === 'vertical' ? 'flex-col' : 'flex-wrap', className)}
    >
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={clsx(
            'flex flex-col items-center gap-2 rounded-lg border-2 p-3 outline-none transition-all',
            value === option.value
              ? 'border-primary-500 ring-2 ring-primary-500/20'
              : 'border-gray-200 dark:border-dark-500 hover:border-gray-300 dark:hover:border-dark-400'
          )}
        >
          {option.preview && <div className="mb-1">{option.preview}</div>}
          <span className="text-sm font-medium">{option.label}</span>
          {option.description && (
            <span className="text-xs text-gray-500 dark:text-dark-300">{option.description}</span>
          )}
        </button>
      ))}
    </div>
  );
}

SettingsForm.displayName = 'SettingsForm';
SettingRow.displayName = 'SettingRow';
SettingToggle.displayName = 'SettingToggle';
SettingOptions.displayName = 'SettingOptions';
